// The footer, per the design: wordmark left, quiet links middle, copyright right.
//
// This is also where ADMIN lives — and only here. Platform admin is for exactly
// one person (the operator), so it has no place in the top nav where it would
// imply everyone has an admin surface. It's a plain link, visible if you know to
// look, and the route is gated by group membership regardless of who finds it.
// Hiding it is a navigation decision, not a security one.

import { Link } from "react-router-dom";
import { c, font, radius } from "../design/tokens.ts";
import { CONSOLE_ORIGIN, DISCORD_INVITE } from "../lib/hosts.ts";
import { useSession } from "../session/SessionProvider.tsx";

const LINKS: Array<{ label: string; to?: string; href?: string }> = [
	{ label: "Docs", to: "/docs" },
	{ label: "Pricing", to: "/pricing" },
	{ label: "How it works", to: "/security" },
	{ label: "llms.txt", href: "/llms.txt" },
	{ label: "Discord", href: DISCORD_INVITE },
	{ label: "Console", href: CONSOLE_ORIGIN },
];

export function Footer() {
	const { isAdmin } = useSession();

	return (
		<footer style={{ borderTop: `1px solid ${c.border}`, background: c.bgAlt }}>
			<div
				className="s3p-footer"
				style={{
					maxWidth: 1120,
					margin: "0 auto",
					padding: "36px 32px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 24,
					flexWrap: "wrap",
				}}
			>
				<Wordmark size={17} />

				<div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
					{LINKS.map((l) =>
						l.to ? (
							<Link
								key={l.label}
								to={l.to}
								style={{ color: c.muted, textDecoration: "none", fontSize: 13 }}
							>
								{l.label}
							</Link>
						) : (
							<a
								key={l.label}
								href={l.href}
								style={{ color: c.muted, textDecoration: "none", fontSize: 13 }}
							>
								{l.label}
							</a>
						),
					)}
					{/* Operator-only. Rendered for admins so they don't have to remember
					    the URL; the route is group-gated either way. */}
					{isAdmin && (
						<a
							href={`${CONSOLE_ORIGIN}/admin`}
							style={{ color: c.faint, textDecoration: "none", fontSize: 13 }}
						>
							Admin
						</a>
					)}
				</div>

				<div style={{ fontFamily: font.mono, fontSize: 12, color: c.faint }}>
					s3p © {new Date().getFullYear()}
				</div>
			</div>
		</footer>
	);
}

/** The wordmark: `s3p_` — the underscore is the cursor, and it's the one place
 *  the accent appears in the chrome. */
export function Wordmark({ size = 21 }: { size?: number }) {
	return (
		<span
			style={{
				fontFamily: font.sans,
				fontWeight: 800,
				fontSize: size,
				letterSpacing: "-0.02em",
				color: c.ink,
			}}
		>
			s3p<span style={{ color: c.blue }}>_</span>
		</span>
	);
}

/** The pill CTA used in the marketing nav. Cross-ORIGIN (the console has its own
 *  host), so a plain anchor — a react-router <Link> would try to route it
 *  client-side and land on a path that doesn't exist on the marketing site. */
export function ConsolePill({ href, label }: { href: string; label: string }) {
	return (
		<a
			href={href}
			style={{
				textDecoration: "none",
				background: c.ink,
				color: c.bg,
				padding: "8px 18px",
				borderRadius: radius.pill,
				fontSize: 14,
				fontWeight: 600,
			}}
		>
			{label}
		</a>
	);
}
